import { cn } from '@/lib/utils';
import { useThemeStore } from '@/stores/theme.store';

type BrandLogoProps = {
  className?: string;
  alt?: string;
  /** Pin to the light asset for surfaces that stay paper-white in both themes
   *  — the form letterhead, which mirrors the server-rendered PDF. */
  paper?: boolean;
};

type BrandWordmarkProps = {
  className?: string;
  align?: 'left' | 'center';
};

export function BrandLogo({ className, alt = 'Crane Safe logo', paper }: BrandLogoProps) {
  const resolved = useThemeStore((state) => state.resolved);
  // The JPG has a baked-in white background, so dark mode gets the transparent
  // PNG badge instead of a white box on navy. Picking the src rather than
  // rendering both keeps one accessible name in the DOM and one download.
  const src = !paper && resolved === 'dark' ? '/cranesafe-logo-dark.png' : '/cranesafe-logo.jpg';

  return (
    <img
      src={src}
      alt={alt}
      className={cn('shrink-0 object-contain', className)}
      draggable={false}
    />
  );
}

export function BrandWordmark({ className, align = 'left' }: BrandWordmarkProps) {
  return (
    <span
      aria-label="CRANESAFE Technologies"
      className={cn(
        'inline-flex flex-col leading-none tracking-normal',
        align === 'center' ? 'items-center text-center' : 'items-start text-left',
        className,
      )}
      style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}
    >
      <span className="inline-flex flex-col">
        <span className="font-normal uppercase leading-none">
          <span className="text-[#e9572d]">CRANE</span>
          <span className="italic text-[#0d6f54]">SAFE</span>
        </span>
        <span aria-hidden="true" className="mt-0.5 flex h-[2px] w-full overflow-hidden">
          <span className="basis-[52%] bg-[#e9572d]" />
          <span className="flex-1 bg-[#0d6f54]" />
        </span>
      </span>
      {/* CRANE/SAFE keep their fixed brand hexes. This line inherits instead of
          naming a colour: it follows the theme on normal surfaces and follows
          the paper letterhead's own `text-black` inside a form. */}
      <span className="mt-1 font-normal leading-none">Technologies</span>
    </span>
  );
}
